import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, errMsg, openPdf } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { exportXlsx } from "@finance/lib/exportCsv";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { toast } from "sonner";
import { DownloadSimple, Sliders, Package, Truck, Warning, Gift, Trash, FileArrowDown } from "@phosphor-icons/react";
import { KpiStrip, KpiCard, EmptyState } from "@finance/components/DesignSystem";
import { InventoryAging } from "@finance/components/InventoryAging";

export default function Inventory() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ direction: "in" });
  const [committed, setCommitted] = useState(null); // { sku, description, list }
  const [writeoffs, setWriteoffs] = useState([]);
  const [woOpen, setWoOpen] = useState(false);
  const [woForm, setWoForm] = useState({ kind: "sample" });

  const load = useCallback(() => api.get("/inventory").then((r) => setRows(r.data)), []);
  const loadWo = useCallback(() => api.get("/inventory/write-offs").then((r) => setWriteoffs(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    loadWo();
    api.get("/items").then((r) => setItems(r.data)).catch(() => {});
  }, [load, loadWo]);

  const saveWriteoff = async () => {
    if (!woForm.sku || !woForm.qty) { toast.error("Item and quantity are required"); return; }
    try {
      await api.post("/inventory/write-off", { kind: woForm.kind, sku: woForm.sku, qty: Number(woForm.qty), recipient: woForm.recipient, reason: woForm.reason });
      toast.success(woForm.kind === "sample" ? "Sample issued & expensed at landed cost" : "Write-off recorded");
      setWoOpen(false); setWoForm({ kind: "sample" }); load(); loadWo();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delWriteoff = async (w) => {
    if (!window.confirm(`Reverse ${w.ref_no}? This restores the stock and reverses the GL.`)) return;
    try { await api.delete(`/inventory/write-offs/${w.id}`); toast.success("Reversed"); load(); loadWo(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const adjust = async () => {
    if (!form.sku || !form.qty || !form.reason) { toast.error("Item, quantity and reason are required"); return; }
    try {
      const it = items.find((x) => x.sku === form.sku);
      await api.post("/inventory/adjust", { sku: form.sku, description: it?.name, qty: Number(form.qty), direction: form.direction, reason: form.reason });
      toast.success("Stock adjustment recorded");
      setOpen(false); setForm({ direction: "in" }); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const showCommitted = async (r) => {
    if (!r.sku || !r.committed) return;
    try {
      const { data } = await api.get(`/inventory/committed`, { params: { sku: r.sku } });
      setCommitted({ sku: r.sku, description: r.description, list: data });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const purgeOrphan = async (r) => {
    if (!window.confirm(`Remove the phantom item "${r.description}"? This deletes only the manual stock adjustment(s) that created it.`)) return;
    try {
      const { data } = await api.delete(`/inventory/orphans`, { params: { key: r.key } });
      toast.success(`Removed phantom item (${data.deleted} adjustment${data.deleted === 1 ? "" : "s"} deleted)`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cols = [
    { key: "sku", label: "SKU" },
    { key: "description", label: "Item" },
    { key: "in_stock", label: "On Hand (In Stock)", format: fmtNum },
    { key: "reserved", label: "Reserved (undelivered)", format: fmtNum },
    { key: "sellable", label: "Sellable (net)", format: fmtNum },
    { key: "in_transit", label: "In-Transit", format: fmtNum },
    { key: "committed", label: "Committed", format: fmtNum },
    { key: "stock_out", label: "Stock Out", format: fmtNum },
  ];

  const orphanCount = rows.filter((r) => r.orphan).length;

  return (
    <Layout>
      <PageHeader
        title="Inventory"
        subtitle={`${rows.length} items · transit → stock → committed → available`}
        actions={
          <>
            <Button variant="outline" data-testid="export-inventory" onClick={() => exportXlsx("inventory", rows, cols)}><DownloadSimple size={16} className="mr-1" />Export</Button>
            <Button variant="outline" data-testid="sample-writeoff-btn" onClick={() => { setWoForm({ kind: "sample" }); setWoOpen(true); }}><Gift size={16} className="mr-1" />Sample / Write-off</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="adjust-stock-btn" onClick={() => setOpen(true)}><Sliders size={16} className="mr-1" />Adjust Stock</Button>
          </>
        }
      />
      <div className="p-8">
        <KpiStrip cols={4}>
          <KpiCard icon={Package} label="Items" value={rows.length} testid="kpi-inv-count" />
          <KpiCard icon={Package} label="Total Available" value={fmtNum(rows.reduce((s, r) => s + (r.in_stock || 0), 0))} tone="success" testid="kpi-inv-available" />
          <KpiCard icon={Truck} label="In-Transit" value={fmtNum(rows.reduce((s, r) => s + (r.in_transit || 0), 0))} tone="info" testid="kpi-inv-transit" />
          <KpiCard icon={Warning} label="Negative Stock" value={rows.filter((r) => r.in_stock < 0).length} tone="danger" testid="kpi-inv-negative" />
        </KpiStrip>
        {orphanCount > 0 && (
          <div className="mb-4 flex items-center gap-2 rounded-sm border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="orphan-banner">
            <Warning size={16} weight="fill" />
            <span>{orphanCount} phantom item{orphanCount === 1 ? "" : "s"} created by past stock adjustments (not in the Item Master). Use the <strong>Delete</strong> action on the highlighted row{orphanCount === 1 ? "" : "s"} below to clean {orphanCount === 1 ? "it" : "them"} up.</span>
          </div>
        )}
        {rows.length === 0 ? (
          <EmptyState icon={Package} testid="inventory-empty" title="No stock activity yet" message='Stock flows in when a holds-stock transaction reaches "Delivery Note Received".' />
        ) : (
        <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
              <th className="px-4 py-2 text-left">SKU</th>
              <th className="px-4 py-2 text-left">Item</th>
              <th className="px-4 py-2 text-right">On Hand (In Stock)</th>
              <th className="px-4 py-2 text-right">Reserved</th>
              <th className="px-4 py-2 text-right">Sellable (net)</th>
              <th className="px-4 py-2 text-right">In-Transit</th>
              <th className="px-4 py-2 text-right">Committed</th>
              <th className="px-4 py-2 text-right">Stock Out</th>
              <th className="px-4 py-2 text-right"></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} data-testid={`inv-row-${r.key}`} className={`border-b border-gray-100 hover:bg-gray-50 ${r.orphan ? "bg-amber-50/60" : ""}`}>
                  <td className="px-4 py-2 font-mono-num text-gray-500">{r.sku ? <button className="text-[#1B4383] hover:underline" data-testid={`item-history-link-${r.sku}`} onClick={() => navigate(`/inventory/item/${encodeURIComponent(r.sku)}`)}>{r.sku}</button> : "—"}</td>
                  <td className="px-4 py-2 font-medium">
                    {r.description}
                    {r.orphan && <span className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`inv-orphan-badge-${r.key}`}>PHANTOM</span>}
                  </td>
                  <td className={`px-4 py-2 text-right lx-num font-semibold ${r.in_stock < 0 ? "text-red-600" : "text-gray-800"}`}>{fmtNum(r.in_stock)}</td>
                  <td className="px-4 py-2 text-right lx-num text-amber-600" data-testid={`inv-reserved-${r.key}`}>{r.reserved ? fmtNum(r.reserved) : "—"}</td>
                  <td className={`px-4 py-2 text-right lx-num font-semibold ${r.sellable < 0 ? "text-red-600" : "text-emerald-700"}`} data-testid={`inv-sellable-${r.key}`}>{fmtNum(r.sellable)}</td>
                  <td className="px-4 py-2 text-right lx-num text-blue-600">{fmtNum(r.in_transit)}</td>
                  <td className="px-4 py-2 text-right lx-num text-amber-600">
                    {r.committed ? (
                      <button data-testid={`inv-committed-${r.key}`} className="font-medium underline decoration-dotted underline-offset-2 hover:text-amber-700" onClick={() => showCommitted(r)}>{fmtNum(r.committed)}</button>
                    ) : fmtNum(r.committed)}
                  </td>
                  <td className="px-4 py-2 text-right lx-num text-gray-600">{fmtNum(r.stock_out)}</td>
                  <td className="px-4 py-2 text-right">
                    {r.orphan && <button className="text-xs text-red-500 hover:underline" data-testid={`inv-orphan-delete-${r.key}`} onClick={() => purgeOrphan(r)}>Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
        <p className="mt-3 text-xs text-gray-500">On Hand = received (GRV) + opening less delivered (DN) · Reserved = committed to stock fulfillments not yet delivered · Sellable = On Hand − Reserved · In Transit = on Somcore PO not yet received · Committed = promised on client PO (click to see which POs).</p>

        {/* Samples & Write-offs */}
        <div className="mt-8 overflow-hidden rounded-sm border border-gray-200 bg-white" data-testid="writeoffs-section">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <div className="font-display text-sm font-semibold text-gray-700">Samples & Write-offs</div>
            <div className="text-xs text-gray-400">{writeoffs.length} record(s) · expensed at landed cost</div>
          </div>
          {writeoffs.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-400">No samples or write-offs recorded. Use “Sample / Write-off” above to expense stock at its landed cost.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
                <th className="px-4 py-2 text-left">Ref</th><th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-left">Item</th><th className="px-4 py-2 text-right">Qty</th>
                <th className="px-4 py-2 text-right">Value (TZS)</th><th className="px-4 py-2 text-left">Recipient / Reason</th>
                <th className="px-4 py-2 text-left">Date</th><th className="px-4 py-2 text-right"></th>
              </tr></thead>
              <tbody>
                {writeoffs.map((w) => (
                  <tr key={w.id} data-testid={`wo-row-${w.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono-num font-medium text-gray-700">{w.ref_no}</td>
                    <td className="px-4 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${w.kind === "sample" ? "bg-blue-100 text-blue-700" : "bg-rose-100 text-rose-700"}`}>{w.kind === "sample" ? "SAMPLE" : "WRITE-OFF"}</span>
                    </td>
                    <td className="px-4 py-2">{w.description}</td>
                    <td className="px-4 py-2 text-right lx-num">{fmtNum(w.qty)}</td>
                    <td className="px-4 py-2 text-right lx-num font-semibold">{fmtNum(w.value)}</td>
                    <td className="px-4 py-2 text-gray-500">{w.recipient || w.reason || "—"}</td>
                    <td className="px-4 py-2 text-gray-500 lx-num">{(w.date || "").slice(0, 10)}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {w.kind === "sample" && <button className="mr-3 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline" data-testid={`wo-pdf-${w.id}`} onClick={() => openPdf(`pdf/sample-delivery-note/${w.id}`)}><FileArrowDown size={13} />Delivery Note</button>}
                      <button className="text-gray-300 hover:text-red-500" data-testid={`wo-del-${w.id}`} onClick={() => delWriteoff(w)}><Trash size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Dialog open={woOpen} onOpenChange={setWoOpen}>
        <DialogContent className="max-w-md" data-testid="writeoff-dialog">
          <DialogHeader><DialogTitle className="font-display">Expense Stock — Sample or Write-off</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="lx-label">Type</Label>
              <Select value={woForm.kind} onValueChange={(v) => setWoForm({ ...woForm, kind: v })}>
                <SelectTrigger className="mt-1" data-testid="wo-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sample">Sample (to a client / prospect)</SelectItem>
                  <SelectItem value="writeoff">Write-off (damage / loss / obsolete)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="lx-label">Item</Label>
              <Select value={woForm.sku || ""} onValueChange={(v) => setWoForm({ ...woForm, sku: v })}>
                <SelectTrigger className="mt-1" data-testid="wo-item"><SelectValue placeholder="Select an item on hand" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {items.map((it) => <SelectItem key={it.id} value={it.sku}>{it.sku} · {it.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-gray-500">Expensed at the item’s current landed (AVCO) cost: Dr {woForm.kind === "sample" ? "Samples & Promotions (6100)" : "Inventory Write-offs (6110)"} / Cr Inventory.</p>
            </div>
            <div><Label className="lx-label">Quantity</Label><Input type="number" data-testid="wo-qty" value={woForm.qty || ""} onChange={(e) => setWoForm({ ...woForm, qty: e.target.value })} className="mt-1" /></div>
            {woForm.kind === "sample" && (
              <div><Label className="lx-label">Recipient (client / prospect)</Label><Input data-testid="wo-recipient" value={woForm.recipient || ""} onChange={(e) => setWoForm({ ...woForm, recipient: e.target.value })} className="mt-1" placeholder="Who is receiving the sample" /></div>
            )}
            <div><Label className="lx-label">{woForm.kind === "sample" ? "Purpose / Notes" : "Reason (required)"}</Label><Input data-testid="wo-reason" value={woForm.reason || ""} onChange={(e) => setWoForm({ ...woForm, reason: e.target.value })} className="mt-1" placeholder={woForm.kind === "sample" ? "e.g. product demo" : "e.g. water damage in store"} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWoOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="wo-save" onClick={saveWriteoff}>Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="adjust-dialog">
          <DialogHeader><DialogTitle className="font-display">Manual Stock Adjustment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="lx-label">Item</Label>
              <Select value={form.sku || ""} onValueChange={(v) => setForm({ ...form, sku: v })}>
                <SelectTrigger className="mt-1" data-testid="adj-item"><SelectValue placeholder="Select an existing item" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {items.map((it) => <SelectItem key={it.id} value={it.sku}>{it.sku} · {it.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-gray-500">Only items in the Item Master can be adjusted. Create new items on the Item Master page.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="lx-label">Quantity</Label><Input type="number" data-testid="adj-qty" value={form.qty || ""} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="mt-1" /></div>
              <div><Label className="lx-label">Direction</Label>
                <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                  <SelectTrigger className="mt-1" data-testid="adj-dir"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="in">Add (in)</SelectItem><SelectItem value="out">Remove (out)</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="lx-label">Reason (required)</Label><Input data-testid="adj-reason" value={form.reason || ""} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1" placeholder="Stock count / damage / write-off" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-adjust" onClick={adjust}>Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!committed} onOpenChange={(v) => { if (!v) setCommitted(null); }}>
        <DialogContent className="max-w-lg" data-testid="committed-dialog">
          <DialogHeader><DialogTitle className="font-display">Committed — {committed?.description}</DialogTitle></DialogHeader>
          <p className="text-xs text-gray-500">Open Client POs still owed this item. Click a PO to open it.</p>
          <div className="mt-2 max-h-80 overflow-y-auto">
            {(committed?.list || []).length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-400">No open client POs.</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="lx-label border-b border-gray-100 text-gray-400">
                  <th className="py-1 text-left">Client PO</th><th className="py-1 text-left">Client</th>
                  <th className="py-1 text-right">Ordered</th><th className="py-1 text-right">Fulfilled</th><th className="py-1 text-right">Remaining</th>
                </tr></thead>
                <tbody>
                  {committed.list.map((c) => (
                    <tr key={c.client_po_id + String(c.description)} data-testid={`committed-po-${c.client_po_id}`} className="cursor-pointer border-b border-gray-50 hover:bg-gray-50" onClick={() => navigate(`/client-pos/${c.client_po_id}`)}>
                      <td className="py-1.5 font-mono-num font-medium text-[#1B4383]">{c.client_po_no}</td>
                      <td className="py-1.5">{c.client_name || "—"}</td>
                      <td className="py-1.5 text-right lx-num">{fmtNum(c.ordered)}</td>
                      <td className="py-1.5 text-right lx-num text-gray-500">{fmtNum(c.fulfilled)}</td>
                      <td className="py-1.5 text-right lx-num font-semibold text-amber-600">{fmtNum(c.remaining)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommitted(null)}>Close</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="committed-goto-pos" onClick={() => navigate("/client-pos")}>Open Client POs</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <InventoryAging />
    </Layout>
  );
}
